import { observer } from "mobx-react-lite";

import SnippetDialog from "@/components/Playground/Messages/SnippetDialog";
import { withSnippetDialog } from "@/components/Playground/Messages/SnippetDialog/snippet-store";
import { ChatHeaderActions } from "@/components/Playground/Drawer/Chat/ChatHeaderActions";
import { ChatInput } from "@/components/Playground/Drawer/Chat/ChatInput";
import { ChatPanel } from "@/components/Playground/Drawer/Chat/ChatPanel";
import {
  ChatWidgetProvider,
  useChatWidgetContext,
} from "@/components/Playground/Drawer/Chat/context";
import { MockToolControls } from "@/components/Playground/Drawer/Chat/MockToolControls";
import { ProviderApiKeyPanel } from "@/components/ProviderApiKeyPrompt";
import { usePlayground } from "@/components/Playground/playground-store";
import { useChatState } from "@/components/Playground/Drawer/Chat/hooks/useChatState";
import useChatWebSocketListeners from "@/components/Playground/Drawer/Chat/hooks/useChatWebSocketListeners";
import { useHydrateChatMessages } from "@/components/Playground/Drawer/Chat/hooks/useHydrateChatMessages";
import { ToolResponsePanel } from "@/components/Playground/Drawer/Chat/ToolResponsePanel";
import DrawerWidget from "@/components/Playground/Drawer/DrawerWidget";

const ChatContent = observer(() => {
  const {
    messages,
    isLoading,
    isStreaming,
    isToolResponseMode,
    pendingToolCalls,
    isApprovalMode,
    pendingApproval,
    sendMessage,
    sendToolResponses,
    editMessage,
    deleteMessage,
    replayMessage,
    approveToolCall,
    retryChatAfterProviderApiKeySaved,
  } = useChatState();
  const { chatProviderApiKeyPrompt } = usePlayground();
  const {
    isLoading: isHydrating,
    isFetchingMore,
    hasMore,
    loadMoreMessages,
  } = useHydrateChatMessages();

  useChatWebSocketListeners();

  let inputArea: React.ReactNode;

  if (isApprovalMode && pendingApproval) {
    inputArea = (
      <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white px-3 py-3 shadow-sm">
        <div className="text-xs text-gray-500">
          <span className="font-medium text-gray-700">{pendingApproval.name}</span> on{" "}
          <span className="font-medium text-gray-700">{pendingApproval.server_label}</span> requires
          approval
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100"
            onClick={() => approveToolCall(false)}
            disabled={isLoading || isStreaming}
          >
            Deny
          </button>
          <button
            type="button"
            className="flex-1 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
            onClick={() => approveToolCall(true)}
            disabled={isLoading || isStreaming}
          >
            Approve
          </button>
        </div>
      </div>
    );
  } else if (isToolResponseMode && pendingToolCalls.length > 0 && !isLoading && !isStreaming) {
    inputArea = (
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
        {/* Keep the mock toggle reachable here too, so a user can enable mocking
            instead of answering the pending tool call by hand. */}
        <div className="mb-2 flex justify-end">
          <MockToolControls standalone />
        </div>
        <ToolResponsePanel
          pendingToolCalls={pendingToolCalls}
          onSubmit={sendToolResponses}
          isLoading={isLoading || isStreaming}
        />
      </div>
    );
  } else {
    inputArea = (
      <ChatInput
        onSend={sendMessage}
        isLoading={isLoading || isStreaming}
        isToolResponseMode={false}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {chatProviderApiKeyPrompt && (
        <div className="border-b border-gray-200 px-3 py-2 dark:border-gray-700">
          <ProviderApiKeyPanel
            provider={chatProviderApiKeyPrompt.provider}
            onAfterProviderKeySaved={retryChatAfterProviderApiKeySaved}
          />
        </div>
      )}

      <ChatPanel
        messages={messages}
        isLoading={isLoading || isHydrating}
        isStreaming={isStreaming}
        isFetchingMore={isFetchingMore}
        hasMore={hasMore}
        onLoadMore={loadMoreMessages}
        onEditMessage={editMessage}
        onDeleteMessage={deleteMessage}
        onReplayMessage={replayMessage}
      />
      <div className="shrink-0 px-3 pb-3 pt-2">{inputArea}</div>
    </div>
  );
});

const ChatInner = observer(() => {
  const { messages, stats, clearChat, isClearing } = useChatState();
  const { containerRef } = useChatWidgetContext();

  const handleClearChat = (
    e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    clearChat();
  };

  return (
    <>
      <DrawerWidget
        name="Chat"
        className="mx-3 flex min-h-0 flex-grow flex-col overflow-hidden border-0 bg-transparent p-0 shadow-none"
        contentClassName="flex min-h-0 flex-grow flex-col"
        childrenClassName="flex min-h-0 flex-grow flex-col"
        count={stats.total_messages}
        isCollapsible={false}
        hideHeader
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      >
        {messages.length > 0 && (
          <div className="mb-2 rounded-lg border border-gray-100 bg-white px-3 py-2">
            <ChatHeaderActions onReset={handleClearChat} isLoading={isClearing} />
          </div>
        )}
        <ChatContent />
      </DrawerWidget>
      <SnippetDialog />
    </>
  );
});

interface ChatProps {
  variableSetIndex?: number;
}

export const Chat = withSnippetDialog(({ variableSetIndex = 0 }: ChatProps) => (
  <ChatWidgetProvider variableSetIndex={variableSetIndex}>
    <ChatInner />
  </ChatWidgetProvider>
));
